import axios from 'axios'
import _get from 'lodash/get'

import {
    ERROR_MESSAGE_DELETING,
    ERROR_MESSAGE_SAVING,
    PATH_SELF_ASSESSMENT_REVIEW,
    PATH_SELF_ASSESSMENT_SOURCES,
    PATH_SELF_ASSESSMENT_SUMMARY,
    SELF_ASSESSMENT_STATUS_ACCOUNTANT_REVIEW,
    SELF_ASSESSMENT_STATUS_CANCELLED,
    SELF_ASSESSMENT_STATUS_DRAFTED,
    TAX_YEARS,
    USER_AGREEMENT_TYPE_SELF_ASSESSMENT,
    VARIANT_AIRBNB,
} from '@/constants'
import { SilentCustomRequestError } from '@/helpers/requestErrors'
import { getAppUrl } from '@/helpers/url-domain'
import { UserAgreementType } from '@/models'
import { track, TRACK_TAX_RETURN_START } from '@/trackers'

import { handleRequestError } from './request'

export const ACTIVATE_ASSESSMENT = 'ACTIVATE_ASSESSMENT'
export const ASSIGN_ASSESSMENT_ACCOUNTANT = 'ASSIGN_ASSESSMENT_ACCOUNTANT'
export const CREATE_AND_ACTIVATE_ASSESSMENT = 'CREATE_AND_ACTIVATE_ASSESSMENT'
export const CREATE_ASSESSMENT = 'CREATE_ASSESSMENT'
export const CREATE_ASSESSMENT_AGREEMENT = 'CREATE_ASSESSMENT_AGREEMENT'
export const DELETE_ASSESSMENT = 'DELETE_ASSESSMENT'
export const RECEIVE_ASSESSMENT = 'RECEIVE_ASSESSMENT'
export const RECEIVE_ASSESSMENT_AGREEMENT = 'RECEIVE_ASSESSMENT_AGREEMENT'
export const REQUEST_ASSESSMENT = 'REQUEST_ASSESSMENT'
export const REQUEST_ASSESSMENT_AGREEMENT = 'REQUEST_ASSESSMENT_AGREEMENT'
export const REQUEST_ASSESSMENT_SUMMARY = 'REQUEST_ASSESSMENT_SUMMARY'
export const SAVE_ASSESSMENT = 'SAVE_ASSESSMENT'
export const SAVE_ASSESSMENT_STATUS = 'SAVE_ASSESSMENT_STATUS'
export const UPDATE_AND_SAVE_ASSESSMENT = 'UPDATE_AND_SAVE_ASSESSMENT'
export const UPDATE_AND_SAVE_ASSESSMENT_STATUS = 'UPDATE_AND_SAVE_ASSESSMENT_STATUS'
export const UPDATE_ASSESSMENT_STATUS = 'UPDATE_ASSESSMENT_STATUS'
export const REQUEST_ASSESSMENT_AGREEMENT_DRAFT = 'REQUEST_ASSESSMENT_AGREEMENT_DRAFT'

export const createAssessmentAndActivate = (data) => (dispatch, getState) => {
    dispatch({ type: CREATE_AND_ACTIVATE_ASSESSMENT, data })

    const { profile } = getState()
    const { taxYear } = data
    const { id: userId } = profile

    return dispatch(createAssessment({ taxYear })).then(({ selfAssessmentId, status }) => {
        track(TRACK_TAX_RETURN_START, { taxYear, userId })

        dispatch(activateAssessment({ selfAssessmentId, status }))
    })
}

export const activateAssessment = (data) => (dispatch, getState) => {
    dispatch({ type: ACTIVATE_ASSESSMENT, data })

    const { profile } = getState()

    const { selfAssessmentId, status } = data

    const redirectTo = () => {
        const { variant } = profile

        if (status === SELF_ASSESSMENT_STATUS_DRAFTED) {
            return PATH_SELF_ASSESSMENT_REVIEW
        }

        if (variant === VARIANT_AIRBNB) return PATH_SELF_ASSESSMENT_SOURCES

        return PATH_SELF_ASSESSMENT_SUMMARY
    }

    window.location.assign(`${getAppUrl(redirectTo())}?activateSelfAssessmentId=${selfAssessmentId}`)
}

export const createAssessment = (data) => (dispatch, getState) => {
    const { assessment, profile } = getState()
    const { isCreating } = assessment
    const { taxYear } = data

    if (isCreating) {
        return Promise.reject(new SilentCustomRequestError())
    }

    dispatch({ type: CREATE_ASSESSMENT })

    const { id: userId } = profile

    return axios
        .post('/selfassessment', { userId, taxYear })
        .then((response) => {
            const responseData = _get(response, ['data'])

            dispatch(receiveAssessment(responseData))

            const selfAssessmentId = _get(responseData, ['id'])
            const status = _get(responseData, ['status'])

            return { ...data, selfAssessmentId, status }
        })
        .catch((error) => {
            dispatch(receiveAssessment({ isCreating: false }))
            dispatch(
                handleRequestError(error, {
                    customMessage: `There was a problem creating a tax return for ${TAX_YEARS[taxYear]}`,
                })
            )
            return Promise.reject(error)
        })
}

export const requestAssessment = (id) => (dispatch) => {
    dispatch({ type: REQUEST_ASSESSMENT, data: { id } })

    return axios
        .get(`/selfassessment/${id}/status`)
        .then((response) => {
            const data = _get(response, ['data'], {})
            dispatch(receiveAssessment({ ...data, fetchSuccessful: true }))
            return data
        })
        .catch((error) => {
            dispatch(receiveAssessment({ fetchSuccessful: false }))
            dispatch(handleRequestError(error))
            return Promise.reject(error)
        })
}

export const requestAssessmentSummary = (id) => (dispatch) => {
    dispatch({ type: REQUEST_ASSESSMENT_SUMMARY, data: { id } })

    return axios
        .get(`/selfassessment/${id}/summary`)
        .then((response) => {
            const data = _get(response, ['data'], {})
            dispatch(receiveAssessment(data))
        })
        .catch((error) => {
            dispatch(handleRequestError(error))
        })
}

export const receiveAssessment = (data) => ({ type: RECEIVE_ASSESSMENT, data })

export const updateAndSaveAssessment = (data) => (dispatch) => {
    dispatch({ type: UPDATE_AND_SAVE_ASSESSMENT, data })

    dispatch(receiveAssessment(data))
    return dispatch(saveAssessment())
}

export const saveAssessment = () => (dispatch, getState) => {
    dispatch({ type: SAVE_ASSESSMENT })

    const { assessment } = getState()

    const { id: selfAssessmentId } = assessment

    return axios
        .put(`/selfassessment/${selfAssessmentId}`, assessment)
        .then((response) => {
            const data = _get(response, ['data'], {})
            dispatch(receiveAssessment(data))
            return data
        })
        .catch((error) => {
            dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_SAVING }))
            return Promise.reject(error)
        })
}

export const updateAndSaveAssessmentStatus =
    (status, { payThroughTaxCodeRequested } = {}) =>
    (dispatch, getState) => {
        dispatch({ type: UPDATE_ASSESSMENT_STATUS, data: { status, payThroughTaxCodeRequested } })

        const { assessment } = getState()

        dispatch(receiveAssessment(assessment))

        const { id: selfAssessmentId } = assessment

        const statusesThatTriggerRedirectToSummary = [
            SELF_ASSESSMENT_STATUS_ACCOUNTANT_REVIEW,
            SELF_ASSESSMENT_STATUS_CANCELLED,
        ]

        dispatch(saveAssessmentStatus({ selfAssessmentId, status, payThroughTaxCodeRequested }))
            .then(() => {
                if (statusesThatTriggerRedirectToSummary.includes(status)) {
                    window.location.assign(getAppUrl(PATH_SELF_ASSESSMENT_SUMMARY))
                }
            })
            .catch(() => {})
    }

export const saveAssessmentStatus = (data) => (dispatch) => {
    dispatch({ type: SAVE_ASSESSMENT_STATUS, data })

    const { selfAssessmentId, status, payThroughTaxCodeRequested } = data
    const body = payThroughTaxCodeRequested != null ? `"${String(payThroughTaxCodeRequested)}"` : ''

    return axios
        .put(`/selfassessment/${selfAssessmentId}/status/${status}`, body, {
            headers: { 'Content-Type': 'application/json' },
        })
        .then((response) => {
            const responseData = _get(response, ['data'], {})
            dispatch(receiveAssessment(responseData))
        })
        .catch((error) => {
            dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_SAVING }))
            return Promise.reject(error)
        })
}

export const deleteAssessment = (id) => (dispatch) => {
    dispatch({ type: DELETE_ASSESSMENT, data: { id } })

    return axios.delete(`/selfassessment/${id}`).catch((error) => {
        dispatch(handleRequestError(error, { customMessage: ERROR_MESSAGE_DELETING }))
        return Promise.reject(error)
    })
}

export const assignAssessmentAccountant = (id) => (dispatch) => {
    dispatch({ type: ASSIGN_ASSESSMENT_ACCOUNTANT, data: { id } })

    return axios.put(`/selfassessment/${id}/accountant`).then((response) => {
        const data = _get(response, ['data'], {})
        const accountantId = _get(data, ['accountantId'])

        if (!accountantId) {
            return Promise.reject(new SilentCustomRequestError())
        }

        dispatch(receiveAssessment(data))

        return Promise.resolve()
    })
}

/**
 * @param {Object} params
 * @param {number} [params.selfAssessmentId]
 * @param {number} [params.subscriptionId]
 * @param {"SelfAssessment" | "AnnualSelfAssessmentSubscription"} [params.userAgreementType]
 */
export const createAssessmentAgreement =
    ({ selfAssessmentId, subscriptionId, userAgreementType = UserAgreementType.SelfAssessment }) =>
    (dispatch, getState) => {
        dispatch({ type: CREATE_ASSESSMENT_AGREEMENT, data: { selfAssessmentId } })
        const { assessment, profile } = getState()
        const { accountantId } = assessment
        const { id: userId } = profile

        return axios
            .post(`/useragreements`, {
                accountantId,
                userId,
                selfAssessmentId,
                subscriptionId,
                userAgreementType,
            })
            .then((response) => {
                if (selfAssessmentId) dispatch(requestAssessment(selfAssessmentId))

                const responseData = _get(response, ['data'], {})
                dispatch(receiveAssessmentAgreement({ agreement: responseData }))
                return responseData
            })
            .catch((error) => {
                const customMessage = 'Unable to sign the terms of engagement. Try again or contact support.'
                dispatch(handleRequestError(error, { customMessage }))
                return Promise.reject(error)
            })
    }

export const requestAssessmentAgreementDraft =
    ({ selfAssessmentId }) =>
    (dispatch, getState) => {
        dispatch({ type: REQUEST_ASSESSMENT_AGREEMENT_DRAFT, data: { selfAssessmentId } })

        const { assessment, profile } = getState()
        const { accountantId } = assessment
        const { id: userId } = profile

        return axios
            .post(`/useragreements/draft`, {
                accountantId,
                userId,
                selfAssessmentId,
                userAgreementType: USER_AGREEMENT_TYPE_SELF_ASSESSMENT,
            })
            .then((response) => {
                const responseData = _get(response, ['data'], {})
                dispatch(receiveAssessmentAgreement({ agreement: responseData }))
                return responseData
            })
            .catch((error) => {
                dispatch(handleRequestError(error))
            })
    }

export const requestAssessmentAgreement =
    ({ selfAssessmentId }) =>
    (dispatch, getState) => {
        dispatch({ type: REQUEST_ASSESSMENT_AGREEMENT, data: { selfAssessmentId } })

        const { assessment, profile } = getState()
        const { accountantId } = assessment
        const { id: userId } = profile

        return axios
            .get(`/selfassessment/${selfAssessmentId}/agreement`, {
                accountantId,
                userId,
                selfAssessmentId,
                userAgreementType: USER_AGREEMENT_TYPE_SELF_ASSESSMENT,
            })
            .then((response) => {
                const responseData = _get(response, ['data'], {})
                dispatch(receiveAssessmentAgreement({ agreement: responseData }))
                return responseData
            })
            .catch((error) => {
                if (_get(error, ['response', 'status']) === 404) {
                    return
                }

                dispatch(handleRequestError(error))
            })
    }

const receiveAssessmentAgreement = (data) => ({ type: RECEIVE_ASSESSMENT_AGREEMENT, data })
